import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const KebabIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor">
    <circle cx="10" cy="4" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="10" cy="16" r="1.6" />
  </svg>
);

// Renders its menu into a portal on <body>, positioned via the trigger's
// bounding rect, so it can never be clipped by an ancestor's overflow
// (e.g. a horizontally-scrollable table wrapper).
const DropdownMenu = ({ trigger, children, menuClassName = "w-52" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const close = () => setIsOpen(false);

  const toggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      close();
    };
    const handleReposition = () => close();
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen]);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className="p-1.5 text-gray-400 rounded-md hover:bg-gray-100 hover:text-gray-600 transition-colors"
      >
        {trigger || <KebabIcon className="w-4 h-4" />}
      </button>
      {isOpen && coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: coords.top, right: coords.right }}
            className={`${menuClassName} z-[1000] bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5`}
            onClick={close}
          >
            <div className="py-1">{children}</div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DropdownMenu;
